import { OHIO_COUNTY_DATA } from './ohio.js';
import { MICHIGAN_COUNTIES } from './michigan.js';
import { PENNSYLVANIA_COUNTIES } from './pennsylvania.js';
import { WEST_VIRGINIA_COUNTIES } from './westVirginia.js';
import { KENTUCKY_COUNTIES } from './kentucky.js';
import { INDIANA_COUNTIES } from './indiana.js';
import { ARIZONA_COUNTIES } from './arizona.js';
import type { CatalogueCounty } from './types.js';

export type { CatalogueCounty } from './types.js';

/**
 * The county catalogue — one source of county facts for the whole application.
 *
 * Source: U.S. Census Bureau, 2020 national county file
 * https://www2.census.gov/geo/docs/reference/codes2020/national_county2020.txt
 *
 * Static data, compiled in. There is deliberately no runtime lookup against the
 * Census or any other network source: a CRM that cannot start because a
 * government host is down would be a poor trade for data that changes roughly
 * never.
 *
 * `server/db/seed.ts` seeds from this and nothing else hard-codes a county
 * list. Adding a state is: add its data file, add it to SUPPORTED_STATES, and
 * restart — seeding is idempotent and never rewrites an existing county's id.
 *
 * Deliberately NOT a general geographic framework. Two states, one shape.
 */

/** States whose counties are supported today. Order is display order. */
export const SUPPORTED_STATES = ['OH', 'MI', 'PA', 'WV', 'KY', 'IN', 'AZ'] as const;
export type SupportedState = (typeof SUPPORTED_STATES)[number];

export const STATE_NAMES: Record<SupportedState, string> = {
  OH: 'Ohio',
  MI: 'Michigan',
  PA: 'Pennsylvania',
  WV: 'West Virginia',
  KY: 'Kentucky',
  IN: 'Indiana',
  AZ: 'Arizona',
};

/** Every catalogue county, grouped by state in SUPPORTED_STATES order. */
export const COUNTY_CATALOGUE: readonly CatalogueCounty[] = [
  ...OHIO_COUNTY_DATA,
  ...MICHIGAN_COUNTIES,
  ...PENNSYLVANIA_COUNTIES,
  ...WEST_VIRGINIA_COUNTIES,
  ...KENTUCKY_COUNTIES,
  ...INDIANA_COUNTIES,
  ...ARIZONA_COUNTIES,
];

/**
 * Expected count per state. A catalogue of any other length is a bug, and the
 * seeder refuses to run against one rather than quietly seeding a short list.
 */
export const COUNTY_COUNTS: Record<SupportedState, number> = {
  OH: 88,
  MI: 83,
  PA: 67,
  WV: 55,
  KY: 120,
  IN: 92,
  AZ: 15,
};

/** Derived from the catalogue, so it cannot disagree with the data. */
export const COUNTY_CATALOGUE_COUNT = COUNTY_CATALOGUE.length;

export function countiesForState(state: string): readonly CatalogueCounty[] {
  return COUNTY_CATALOGUE.filter((c) => c.state === state);
}

/**
 * Reduce a county name to the key used for lookup and comparison.
 * "Van Wert", "van wert", "VAN WERT COUNTY" and "Van  Wert County" all agree.
 */
export function countyLookupKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\s+county$/, '');
}

export function normalizeStateCode(state: string | null | undefined): string | null {
  if (!state) return null;
  const trimmed = state.trim().toUpperCase();
  return trimmed.length === 2 ? trimmed : null;
}

/**
 * County names that exist in more than one supported state.
 *
 * This is derived, never hand-written: adding a state immediately widens the
 * set, so a name that is unambiguous today and shared tomorrow starts warning
 * without anyone remembering to update a list. Currently: Clinton, Crawford,
 * Huron, Jackson, Lake, Monroe, Ottawa, Wayne.
 */
export const AMBIGUOUS_COUNTY_NAMES: ReadonlyMap<string, readonly string[]> = (() => {
  const byKey = new Map<string, string[]>();
  for (const county of COUNTY_CATALOGUE) {
    const key = countyLookupKey(county.name);
    const states = byKey.get(key) ?? [];
    if (!states.includes(county.state)) states.push(county.state);
    byKey.set(key, states);
  }
  return new Map([...byKey].filter(([, states]) => states.length > 1));
})();

/** The states a bare county name could refer to, or [] if it is unique/unknown. */
export function ambiguousStatesFor(name: string): readonly string[] {
  return AMBIGUOUS_COUNTY_NAMES.get(countyLookupKey(name)) ?? [];
}
